/* eslint-disable consistent-return */
/* eslint-disable no-useless-return */
/* eslint-disable no-async-promise-executor */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isLoading } from '../modules/app';
import { RootState } from '../rootReducer';
import AwsAuthService from '../services/aws-auth-service';
import McsBackendService from '../services/mcs-backend-service';
import { AppDispatch } from '../store';
import ApiCommon from '../utils/api-common';
import BackendClient from '../utils/backend-client';
import { isKonyApp } from '../utils/device';
import { handleCommonError } from '../utils/handle-errors';
import { transferToNativeMethod } from '../utils/transferToNativeMethod';

const Chatbot: React.FC = () => {
  const { loggedIn } = useSelector((state: RootState) => state.user);
  const chatBotScript = process.env.REACT_APP_CHATBOT_SCRIPT_SRC || '';
  const { pathname } = useLocation();
  const dispatch: AppDispatch = useDispatch();

  const pointerPositionRef = useRef<{ x: number; y: number } | null>(null);

  const [tagLoad, setTagLoad] = useState({ icon: false, analytics: false, popup: false });
  const [isOpenChatWindow, setIsOpenChatWindow] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const chatbotScriptId = 'chatbot';
  const chatBotEventOrigin = process.env.REACT_APP_CHATBOT_EVENT_ORIGIN || '';

  // 初期化処理
  useEffect(() => {
    init();
  }, [loggedIn]);

  const init = async () => {
    const deleteTag = async () => {
      const existingScript = document.getElementById(chatbotScriptId);
      if (!existingScript) return;

      if (!loggedIn) {
        window.location.reload();
      }

      const elementsToRemove = [
        existingScript,
        document.querySelector('.krkr-popup-icon-wrapper')?.parentElement,
        document.querySelector('.krkr-modern-chat')?.parentElement,
        document.querySelector('script[data-karakuri-tracking]'),
        ...Array.from(document.querySelectorAll<HTMLElement>('style')).filter((el) => el.textContent?.includes('.krkr-chat')),
      ].filter(Boolean) as HTMLElement[];

      elementsToRemove.forEach((el) => el.remove());

      delete (window as any).krkrExternal;
      delete (window as any).krkrTrack;
      setTagLoad({ icon: false, analytics: false, popup: false });
    };

    const createTag = async () => {
      if (!loggedIn) return;
      const popupScript = document.createElement('script');
      popupScript.src = `${chatBotScript}?t=${Date.now()}`;
      popupScript.id = chatbotScriptId;
      const memberNo = await AwsAuthService.getUsername();

      if (loggedIn && memberNo) {
        const session = memberNo + Date.now().toString();
        try {
          const response = await BackendClient.httpPost({
            body: { memberNo },
            endpoint: '/chatbot/token',
          });
          const json = await response.json();
          if (!response.ok) throw json;

          popupScript.setAttribute('data-session', session);
          popupScript.setAttribute('data-token', json.token);
        } catch (error) {
          return;
        }
      }

      document.body.appendChild(popupScript);
    };

    await deleteTag();
    await createTag();
  };

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type !== 'childList') return;

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          // チャットボットの画面生成を検知
          if (node.classList.contains('krkr-modern-chat')) {
            setIsOpenChatWindow(true);
          }

          // アイコン生成を検知
          if (node.classList.contains('krkr-popup-icon-wrapper')) {
            setTagLoad((prev) => ({ ...prev, icon: true }));
          }

          if (node.tagName.toLowerCase() !== 'script') return;

          const { src } = (node as HTMLScriptElement);
          const popupJsTag = `${chatBotEventOrigin.replace('https://', '')}`;
          const karakuriAnalyticsTag = `analytics.${chatBotEventOrigin.replace('https://lake.', '')}`;

          // アナリティクスタグ生成を検知
          if (src.includes(karakuriAnalyticsTag)) {
            setTagLoad((prev) => ({ ...prev, analytics: true }));
          }

          // popup.js生成を検知
          if (src.includes(popupJsTag)) {
            setTagLoad((prev) => ({ ...prev, popup: true }));
          }
        });
        mutation.removedNodes.forEach((node) => {
          // チャットボット画面の削除を検知
          if (node instanceof HTMLElement && node.classList.contains('krkr-modern-chat')) {
            dispatch(isLoading(false));
          }
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  // OPEN
  useEffect(() => {
    const { icon, analytics, popup } = tagLoad;
    if (!icon || !analytics || !popup) return;

    const chatbotOpen = document.querySelector('.krkr-button-img');

    headerZIndex('100000');
    chatbotOpen?.addEventListener('pointerdown', handlePointerDown, { passive: true });
    chatbotOpen?.addEventListener('pointerup', handlePointerUp, { passive: true });
    return () => {
      chatbotOpen?.removeEventListener('pointerdown', handlePointerDown);
      chatbotOpen?.removeEventListener('pointerup', handlePointerUp);
    };
  }, [tagLoad, pathname]);

  // 画面遷移時にKony関数 Close呼び出し
  useEffect(() => {
    if (isKonyApp()) {
      transferToNativeMethod('CHATBOT_CLOSE');
    }
  }, [pathname]);

  const handlePointerDown = (e: Event) => {
    const clickedElment = e.target as HTMLElement;
    if (clickedElment.tagName === 'IMG' || clickedElment.tagName === 'DIV') {
      const pointerEvent = e as PointerEvent;
      pointerPositionRef.current = { x: pointerEvent.clientX, y: pointerEvent.clientY };
    } else {
      pointerPositionRef.current = null;
    }
  };

  const handlePointerUp = async (e:Event) => {
    if (!pointerPositionRef.current) return;

    const threshold = 5;
    const pointerEvent = e as PointerEvent;
    const movedX = Math.abs(pointerEvent.clientX - pointerPositionRef.current.x);
    const movedY = Math.abs(pointerEvent.clientY - pointerPositionRef.current.y);

    if (movedX >= threshold || movedY >= threshold) {
      pointerPositionRef.current = null;
      return;
    }

    dispatch(isLoading(true));
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    if (isPending) return;
    try {
      if (!loggedIn) return;
      setIsPending(true);
      await delay(500);
      const commonPart = await ApiCommon.getCommonPart('T001', 'T-23', pathname, 'A');
      const dataPart = { データ部: { 会員番号: await AwsAuthService.getUsername() } };
      await McsBackendService.request(Object.assign(commonPart, dataPart));
    } catch (err) {
      handleErrors(err);
    } finally {
      if (isKonyApp()) {
        transferToNativeMethod('CHATBOT_OPEN');
      }
      headerZIndex('99');
      setIsPending(false);
      dispatch(isLoading(false));
      pointerPositionRef.current = null;
    }
  };

  // CLOSE
  useEffect(() => {
    if (!isOpenChatWindow) return;
    const chatbotClose = document.querySelector('.krkr-modern-chat__head');
    chatbotClose?.addEventListener('mouseup', sendCloseEvent);
    return () => {
      chatbotClose?.removeEventListener('mouseup', sendCloseEvent);
    };
  }, [isOpenChatWindow]);

  const sendCloseEvent = async () => {
    setIsOpenChatWindow(false);
    if (isKonyApp()) {
      transferToNativeMethod('CHATBOT_CLOSE');
    }
    headerZIndex('100000');
    // チャットボットクローズボタン押下から、画面の要素が消えるまでローディング
    dispatch(isLoading(true));
  };

  // window.postMessage()処理
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // チャットボットからの通信以外は処理を行わない
      if (event.origin !== chatBotEventOrigin) return;
      if (event.data.payload?.account_type !== 'kaiin') return;
      // 有人チャット開始処理
      const staffChatStart = async (startDate: string, historyUrl: string) => {
        try {
          const commonPart = await ApiCommon.getCommonPart('T001', 'T-24', pathname, 'A');
          const dataPart = { データ部: { 有人チャット接続区分: '0', 有人チャット接続日時: startDate, 会話履歴URL: historyUrl } };
          await McsBackendService.request(Object.assign(commonPart, dataPart));
        } catch (err) {
          handleErrors(err);
        }
      };
      // 有人チャット終了処理
      const staffChatEnd = async (endDate: string, historyUrl: string) => {
        try {
          const commonPart = await ApiCommon.getCommonPart('T001', 'T-25', pathname, 'A');
          const dataPart = { データ部: { 有人チャット接続区分: '1', 有人チャット終了日時: endDate, 会話履歴URL: historyUrl } };
          await McsBackendService.request(Object.assign(commonPart, dataPart));
        } catch (err) {
          return;
        }
      };
      if (event.data.method === 'staffChatStart') {
        staffChatStart(
          event.data.payload.start_date,
          event.data.payload.history_url,
        );
      } else if (event.data.method === 'staffChatEnd') {
        staffChatEnd(
          event.data.payload.end_date,
          event.data.payload.history_url,
        );
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // reload処理
  useEffect(() => {
    const handleReload = () => {
      if (isKonyApp()) {
        transferToNativeMethod('CHATBOT_CLOSE');
      }
    };
    window.addEventListener('beforeunload', handleReload);
    return () => {
      window.removeEventListener('beforeunload', handleReload);
    };
  }, []);

  return null;
};
const handleErrors = (err: any) => {
  handleCommonError(err);
  setTimeout(() => {
    const chatHeadElement = document.querySelector('.krkr-modern-chat__head');
    if (chatHeadElement instanceof HTMLElement) {
      chatHeadElement.click();
    }
  }, 500);
};
const headerZIndex = (zIndexValue: string) => {
  const headerElement = document.getElementById('header');
  if (headerElement) {
    headerElement.style.zIndex = zIndexValue;
  }
}
export default Chatbot;
